import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

export interface FieldValidationConfig {
  id: string;
  user_id: string;
  field_name: string;
  display_name: string;
  is_mandatory: boolean;
  field_type: 'text' | 'number' | 'date' | 'email' | 'phone' | 'currency' | 'percentage' | 'select' | 'boolean';
  validation_rules: any;
  default_value?: string;
  help_text?: string;
  field_group: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateFieldConfigData {
  field_name: string;
  display_name: string;
  is_mandatory: boolean;
  field_type: FieldValidationConfig['field_type'];
  validation_rules?: any;
  default_value?: string;
  help_text?: string;
  field_group?: string;
  sort_order?: number;
  is_active?: boolean;
}

export interface UpdateFieldConfigData {
  display_name?: string;
  is_mandatory?: boolean;
  field_type?: FieldValidationConfig['field_type'];
  validation_rules?: any;
  default_value?: string;
  help_text?: string;
  field_group?: string;
  sort_order?: number;
  is_active?: boolean;
}

export const useFieldValidationConfigs = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch all field validation configs
  const configsQuery = useQuery({
    queryKey: ['field-validation-configs', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase.functions.invoke('field-validation-manager', {
        body: {
          action: 'list',
          user_id: user.id
        }
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Failed to fetch configurations');
      
      return data.data as FieldValidationConfig[];
    },
    enabled: !!user?.id,
  });

  // Create field config mutation
  const createConfigMutation = useMutation({
    mutationFn: async (configData: CreateFieldConfigData) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase.functions.invoke('field-validation-manager', {
        body: {
          action: 'create',
          data: configData,
          user_id: user.id
        }
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Failed to create configuration');
      
      return data.data;
    },
    onSuccess: () => {
      toast({ title: "Field configuration created", description: "Field validation config has been created successfully" });
      queryClient.invalidateQueries({ queryKey: ['field-validation-configs'] });
    },
    onError: (error: any) => {
      toast({ title: "Failed to create field config", description: error.message, variant: "destructive" });
    },
  });

  // Update field config mutation
  const updateConfigMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateFieldConfigData }) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data: result, error } = await supabase.functions.invoke('field-validation-manager', {
        body: {
          action: 'update',
          id,
          data,
          user_id: user.id
        }
      });

      if (error) throw error;
      if (!result.success) throw new Error(result.error || 'Failed to update configuration');
      
      return result.data;
    },
    onSuccess: () => {
      toast({ title: "Field configuration updated", description: "Field validation config has been updated successfully" });
      queryClient.invalidateQueries({ queryKey: ['field-validation-configs'] });
    },
    onError: (error: any) => {
      toast({ title: "Failed to update field config", description: error.message, variant: "destructive" });
    },
  });

  // Delete field config mutation
  const deleteConfigMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase.functions.invoke('field-validation-manager', {
        body: {
          action: 'delete',
          id,
          user_id: user.id
        }
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Failed to delete configuration');
    },
    onSuccess: () => {
      toast({ title: "Field configuration deleted", description: "Field validation config has been deleted successfully" });
      queryClient.invalidateQueries({ queryKey: ['field-validation-configs'] });
    },
    onError: (error: any) => {
      toast({ title: "Failed to delete field config", description: error.message, variant: "destructive" });
    },
  });

  // Toggle mandatory status
  const toggleMandatoryMutation = useMutation({
    mutationFn: async ({ id, is_mandatory }: { id: string; is_mandatory: boolean }) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase.functions.invoke('field-validation-manager', {
        body: {
          action: 'toggle_mandatory',
          id,
          data: { is_mandatory },
          user_id: user.id
        }
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Failed to update field requirement');
      
      return data.data;
    },
    onSuccess: (_, variables) => {
      toast({ 
        title: `Field ${variables.is_mandatory ? 'made mandatory' : 'made optional'}`, 
        description: `Field validation requirement has been updated` 
      });
      queryClient.invalidateQueries({ queryKey: ['field-validation-configs'] });
    },
    onError: (error: any) => {
      toast({ title: "Failed to update field requirement", description: error.message, variant: "destructive" });
    },
  });

  return {
    configs: configsQuery.data || [],
    mandatoryConfigs: configsQuery.data?.filter(config => config.is_mandatory && config.is_active) || [],
    configsByGroup: configsQuery.data?.reduce((acc, config) => {
      const group = config.field_group || 'general';
      if (!acc[group]) acc[group] = [];
      acc[group].push(config);
      return acc;
    }, {} as Record<string, FieldValidationConfig[]>) || {},
    isLoading: configsQuery.isLoading,
    error: configsQuery.error,
    createConfig: (data: CreateFieldConfigData) => createConfigMutation.mutate(data),
    updateConfig: (id: string, data: UpdateFieldConfigData) => updateConfigMutation.mutate({ id, data }),
    deleteConfig: (id: string) => deleteConfigMutation.mutate(id),
    toggleMandatory: (id: string, is_mandatory: boolean) => toggleMandatoryMutation.mutate({ id, is_mandatory }),
    isCreating: createConfigMutation.isPending,
    isUpdating: updateConfigMutation.isPending,
    isDeleting: deleteConfigMutation.isPending,
    isToggling: toggleMandatoryMutation.isPending,
  };
};